import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { Alert } from '@src/components';
import YesNoInput from '@src/components/YesNoInput';
import { useUpdateNifOrderMutation } from '@src/generated/graphql';
import { useStatus } from '@src/hooks/useStatus';
import { logAndFormatError } from '@src/services/error-formatter';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import { NifOrderStepPageProps } from '../types/stepProps';

// t('nif:is_portuguese.response_required_error', 'Response is required'))
const formSchema = z.object({
  isPortugueseCitizen: z.boolean({
    required_error: 'nif:is_portuguese.response_required_error',
  }),
});

type FormData = z.infer<typeof formSchema>;

function NifIsPortuguesePage({
  nifOrder,
  previousUrl,
  getNextUrl,
}: NifOrderStepPageProps): JSX.Element {
  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      isPortugueseCitizen:
        nifOrder.isPortugueseCitizen == null
          ? undefined
          : nifOrder.isPortugueseCitizen,
    },
    resolver: zodResolver(formSchema),
  });
  const [updateNifOrder] = useUpdateNifOrderMutation();
  const { status, setError } = useStatus();
  const navigate = useNavigate();
  const { t } = useTranslation(['nif']);

  function onSubmit(formData: FormData): void {
    updateNifOrder({
      variables: {
        input: {
          id: nifOrder.id,
          data: formData,
        },
      },
    })
      .then((updatedOrder) => {
        const updatedNifOrder = updatedOrder?.data?.updateNifOrder?.nifOrder;
        if (!updatedNifOrder) {
          throw new Error('updateNifOrder returned null');
        }

        void navigate(getNextUrl(updatedNifOrder));
      })
      .catch((err) => setError(logAndFormatError(err)));
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <h2>
        {t(
          'is_portuguese.question',
          'Does the applicant hold Portuguese citizenship?',
        )}
      </h2>
      <Alert.WithStatus status={status} />
      <YesNoInput.LabelledController
        control={control}
        name="isPortugueseCitizen"
      />
      <OrderNavigationButtons previousUrl={previousUrl} buttonType="submit" />
    </form>
  );
}

export default NifIsPortuguesePage;
